import React, {Component, useState} from 'react';
import {connect} from 'react-redux';
import {increment, decrement} from '../../features/counter/counterSlice';
import {setLoggedIn} from '../../features/account/loginSlice';
import {setAccount} from '../../features/account/accountSlice';
import {setCompany} from '../../features/account/companySlice';
import axios from 'axios';
import qs from 'qs';
import Swal from 'sweetalert2';
import {withTranslation} from 'react-i18next';

// MUI icons
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import CheckCircle from '@mui/icons-material/CheckCircle';
import RadioButtonUnchecked from '@mui/icons-material/RadioButtonUnchecked';

// Map hardcoded backend rejection strings to i18n keys. Only the two
// realistic cases are listed — everything else the backend rejects is
// also blocked by the frontend validator, so users don't normally see
// it. Anything unmapped falls through to the raw backend text.
const BACKEND_ERROR_KEYS = {
    'old password does not match': 'setting.changePassword.errors.badOldPassword',
    'password must not match any of your recent previous passwords': 'setting.changePassword.errors.reusedRecent',
};

function translateBackendError(t, message) {
    if (!message) return t('setting.changePassword.failed');
    const key = BACKEND_ERROR_KEYS[message.toLowerCase().trim()];
    return key ? t(key) : message;
}

const COMMON_WEAK_PASSWORDS = new Set([
    'password', 'password1', 'password12', 'password123', 'password1234',
    'passw0rd', 'p@ssword', 'p@ssw0rd',
    'qwerty', 'qwerty1', 'qwerty12', 'qwerty123', 'qwertyuiop',
    '12345678', '123456789', '1234567890', '11111111', '00000000',
    'letmein', 'letmein1', 'welcome', 'welcome1', 'welcome123',
    'admin', 'admin1', 'admin123', 'administrator',
    'iloveyou', 'sunshine', 'monkey1', 'football',
    'trucarbon', 'trucarbon1', 'trucarbon123',
    'abc12345', 'abcdefgh',
]);

const containsIdentityString = (pwd, s) => {
    const str = (s || '').toLowerCase().trim();
    if (str.length < 3) return false;
    return pwd.toLowerCase().includes(str);
};

const containsEmailLocalPart = (pwd, email) => {
    const local = (email || '').split('@')[0].toLowerCase().trim();
    if (local.length < 3) return false;
    return pwd.toLowerCase().includes(local);
};

// Checks the SLD (2nd-level domain) of the user's email — e.g. "trucarbon"
// from "shirad@trucarbon.co". Approximates a company-name signal even when
// no client_name is set.
const containsEmailDomain = (pwd, email) => {
    const domain = (email || '').split('@')[1]?.split('.')[0]?.toLowerCase().trim();
    if (!domain || domain.length < 3) return false;
    return pwd.toLowerCase().includes(domain);
};

// Rejects any 6+ consecutive digits from the user's phone number appearing
// (as digits) anywhere in the password. Handles varying phone formats
// (with/without country code, punctuation) by stripping non-digits from
// both sides before matching.
const containsPhoneNumber = (pwd, phone) => {
    const phoneDigits = String(phone || '').replace(/\D/g, '');
    if (phoneDigits.length < 6) return false;
    const pwdDigits = pwd.replace(/\D/g, '');
    for (let i = 0; i <= phoneDigits.length - 6; i++) {
        if (pwdDigits.includes(phoneDigits.substring(i, i + 6))) return true;
    }
    return false;
};

// Detects a run of >= threshold consecutive characters whose codes are all
// ascending (+1), descending (-1), or repeating (0). Catches "1234", "dcba",
// "aaaa", and the same patterns embedded inside longer passwords.
const hasSequentialRun = (str, threshold = 4) => {
    const s = (str || '').toLowerCase();
    if (s.length < threshold) return false;
    let runLen = 1;
    let runDelta = null;
    for (let i = 1; i < s.length; i++) {
        const delta = s.charCodeAt(i) - s.charCodeAt(i - 1);
        if (delta === 1 || delta === -1 || delta === 0) {
            if (delta === runDelta) {
                runLen++;
            } else {
                runDelta = delta;
                runLen = 2;
            }
            if (runLen >= threshold) return true;
        } else {
            runDelta = null;
            runLen = 1;
        }
    }
    return false;
};

/* ===========================
   Reusable PasswordInput (Bootstrap input-group; no inline styles)
   =========================== */
function PasswordInput({
                           id,
                           label,
                           value,
                           onChange,
                           placeholder = '',
                           name,
                           autoComplete,
                           error = false,
                           helperText = '',
                           disabled = false,
                       }) {
    const [show, setShow] = useState(false);
    // only set aria-describedby when helper text exists
    const describedById = error && helperText ? `${id}-helper` : undefined;

    return (
        <div>
            {label && (
                <label htmlFor={id} className="form-label">
                    {label}
                </label>
            )}

            <div className="input-group">
                <input
                    id={id}
                    name={name}
                    type={show ? 'text' : 'password'}
                    className={`form-control ${error ? 'is-invalid' : ''}`}
                    placeholder={placeholder}
                    value={value}
                    onChange={onChange}
                    autoComplete={autoComplete}
                    aria-invalid={error ? 'true' : 'false'}
                    aria-describedby={describedById}
                    disabled={disabled}
                />
                <button
                    type="button"
                    className="btn btn-outline-secondary"
                    aria-label={show ? 'Hide password' : 'Show password'}
                    aria-pressed={show ? 'true' : 'false'}
                    onClick={() => setShow((s) => !s)}
                    tabIndex={-1}
                >
                    {show ? <VisibilityOff/> : <Visibility/>}
                </button>
            </div>

            {/* Helper text only if provided */}
            {error && helperText && (
                <div id={describedById} className="invalid-feedback d-block mt-1">
                    {helperText}
                </div>
            )}
        </div>
    );
}

/* ===========================
   Password Strength Meter
   Simple 0-4 bucket scoring. Rewards length + character variety, penalises
   the same anti-patterns validateNewPassword catches (weak dictionary,
   sequential runs, all-digits). Purely presentational — it does NOT gate
   submit; the checklist + inline error do that.
   =========================== */
function computePasswordStrength(pwd) {
    if (!pwd) return { bucket: 0, filled: 0 };

    let score = 0;
    if (pwd.length >= 8) score += 1;
    if (pwd.length >= 10) score += 1;
    if (pwd.length >= 14) score += 1;

    if (/[a-z]/.test(pwd)) score += 1;
    if (/[A-Z]/.test(pwd)) score += 1;
    if (/\d/.test(pwd)) score += 1;
    if (/[^A-Za-z0-9]/.test(pwd)) score += 1;

    if (COMMON_WEAK_PASSWORDS.has(pwd.toLowerCase())) score = 0;
    else if (/^\d+$/.test(pwd)) score = Math.min(score, 1);
    else if (hasSequentialRun(pwd, 4)) score = Math.min(score, 2);

    // 0-2 pts -> Weak (1 bar), 3-4 -> Fair (2), 5-6 -> Good (3), 7+ -> Strong (4)
    let filled = 1;
    if (score >= 7) filled = 4;
    else if (score >= 5) filled = 3;
    else if (score >= 3) filled = 2;
    return { bucket: filled, filled };
}

function PasswordStrengthMeter({ pwd, t }) {
    if (!pwd) return null;
    const { filled } = computePasswordStrength(pwd);
    const labelKey = ['weak', 'weak', 'fair', 'good', 'strong'][filled];
    const colors = {
        weak: '#dc3545',
        fair: '#fd7e14',
        good: '#ffc107',
        strong: '#28a745',
    };
    const color = colors[labelKey];
    return (
        <div className="mt-2">
            <div className="d-flex gap-1" style={{ height: 4 }}>
                {[1, 2, 3, 4].map(i => (
                    <div
                        key={i}
                        className="flex-fill rounded"
                        style={{ background: i <= filled ? color : '#e5e7eb' }}
                    />
                ))}
            </div>
            <small className="d-block mt-1" style={{ color, fontWeight: 500 }}>
                {t(`setting.changePassword.strength.${labelKey}`, labelKey.charAt(0).toUpperCase() + labelKey.slice(1))}
            </small>
        </div>
    );
}

/* ===========================
   Password Checklist (Bootstrap list + MUI icons; no inline styles)
   =========================== */
function PasswordChecklist({meetsLength, hasUpper, hasSpecial, t}) {
    const Row = ({ok, children}) => (
        <li className={`d-flex align-items-center gap-2 ${ok ? 'text-success' : 'text-muted'}`}>
            {ok ? <CheckCircle fontSize="small"/> : <RadioButtonUnchecked fontSize="small"/>}
            <span>{children}</span>
        </li>
    );

    return (
        <ul className="list-unstyled small mb-2 mt-2">
            <Row ok={meetsLength}>{t('setting.changePassword.meetsLength')}</Row>
            <Row ok={hasUpper}>{t('setting.changePassword.hasUpper')}</Row>
            <Row ok={hasSpecial}>{t('setting.changePassword.hasSpecial')}</Row>
        </ul>
    );
}

/* ===========================
   Page Component
   =========================== */
export class SettingChangePassword extends Component {
    constructor(props) {
        super(props);
        this.state = {
            inputPasswordOld: '',
            inputPasswordNew: '',
            inputPasswordConfirm: '',

            // live validations
            passwordMismatch: false,

            newPwdInvalid: false,
            newPwdChecks: {
                meetsLength: false,
                hasUpper: false,
                hasSpecial: false,
                notAllDigits: false,
                notSequential: false,
                notCommonWeak: false,
                noIdentityInPwd: false,
                differsFromOld: false,
                ok: false,
                errors: [],
                msg: '',
            },
        };
    }

    // --- validation helpers ---
    validateNewPassword = (pwd, oldPwd = '') => {
        const meetsLength = pwd.length >= 8;
        const hasUpper = /[A-Z]/.test(pwd);
        const hasSpecial = /[^A-Za-z0-9]/.test(pwd);
        const notAllDigits = pwd.length > 0 && !/^\d+$/.test(pwd);
        const notSequential = pwd.length > 0 && !hasSequentialRun(pwd, 4);
        const notCommonWeak = pwd.length > 0 && !COMMON_WEAK_PASSWORDS.has(pwd.toLowerCase());

        const account = this.props.account || {};
        const hasName = pwd.length > 0 && containsIdentityString(pwd, account.user_name);
        const hasCompany = pwd.length > 0 && containsIdentityString(pwd, account.client_name);
        const hasEmail = pwd.length > 0 && containsEmailLocalPart(pwd, account.user_email);
        const hasDomain = pwd.length > 0 && containsEmailDomain(pwd, account.user_email);
        const hasPhone = pwd.length > 0 && containsPhoneNumber(pwd, account.user_phone_number);
        const noIdentityInPwd = !hasName && !hasCompany && !hasEmail && !hasDomain && !hasPhone;

        const differsFromOld = !(oldPwd && pwd && pwd === oldPwd);

        const ok = meetsLength && hasUpper && hasSpecial
            && notAllDigits && notSequential && notCommonWeak && noIdentityInPwd
            && differsFromOld;

        // Only the "must not" rules are surfaced in the inline error list —
        // the "must include" rules (length, capital, special) are already
        // shown by PasswordChecklist below the input, so putting them in
        // both places just makes the empty-password state look alarming.
        // Wording mirrors frontend-account's register/forgot-password inline
        // errors for consistency across all password entry surfaces.
        const errors = [];
        if (!notAllDigits) errors.push('Please avoid using only numbers.');
        if (!notSequential) errors.push('Please avoid obvious sequences like 1234 or abcd.');
        if (!notCommonWeak) errors.push('This password is too common. Please choose another one.');
        if (hasName) errors.push('Please do not use your name in your password.');
        if (hasCompany) errors.push('Please do not use your company name in your password.');
        if (hasEmail) errors.push('Please do not use your email address in your password.');
        if (hasDomain) errors.push('Please do not use your company or email domain in your password.');
        if (hasPhone) errors.push('Please do not use your phone number in your password.');
        if (!differsFromOld) errors.push('Please do not reuse your current password.');

        return {
            meetsLength,
            hasUpper,
            hasSpecial,
            notAllDigits,
            notSequential,
            notCommonWeak,
            noIdentityInPwd,
            differsFromOld,
            ok,
            errors: ok ? [] : errors,
            msg: ok ? '' : errors.join(' '),
        };
    };

    updateMismatch = () => {
        const {inputPasswordNew, inputPasswordConfirm} = this.state;
        const passwordMismatch =
            inputPasswordNew !== '' &&
            inputPasswordConfirm !== '' &&
            inputPasswordNew !== inputPasswordConfirm;
        this.setState({passwordMismatch});
    };

    // --- input handlers ---
    onInputPasswordOld = (e) => {
        const oldPwd = e.target.value;
        const checks = this.validateNewPassword(this.state.inputPasswordNew, oldPwd);
        this.setState({
            inputPasswordOld: oldPwd,
            newPwdInvalid: this.state.inputPasswordNew !== '' && !checks.ok,
            newPwdChecks: checks,
        });
    };

    onInputPasswordNew = (e) => {
        const pwd = e.target.value;
        const checks = this.validateNewPassword(pwd, this.state.inputPasswordOld);
        this.setState(
            {
                inputPasswordNew: pwd,
                newPwdInvalid: pwd !== '' && !checks.ok,
                newPwdChecks: checks,
            },
            this.updateMismatch
        );
    };

    onInputPasswordConfirm = (e) => {
        this.setState({inputPasswordConfirm: e.target.value}, this.updateMismatch);
    };

    // --- API ---
    onChangePasswordCall = async (data) => {
        const options = {
            withCredentials: true,
            method: 'POST',
            headers: {'content-type': 'application/x-www-form-urlencoded'},
            data: qs.stringify(data),
            url: `${process.env.REACT_APP_BASE_SERVER}/account/setting/change_password`
        };
        try {
            await axios(options);
            return { ok: true };
        } catch (e) {
            return { ok: false, message: e?.response?.data?.message || '' };
        }
    }

    // --- submit ---
    onChangePassword = async () => {
        const {
            inputPasswordOld: oldPw,
            inputPasswordNew: newPw,
            newPwdInvalid,
            newPwdChecks,
        } = this.state;

        // required checks
        // new password policy (still enforced; no helper text on field)
        if (newPwdInvalid) {
            Swal.fire({
                title: '',
                html: `<p>${newPwdChecks.msg}</p>`,
                showCancelButton: false,
                confirmButtonColor: '#3085d6',
                confirmButtonText: 'Ok',
            });
            return;
        }

        // submit
        const data = {passwordOld: oldPw, passwordNew: newPw};
        const result = await this.onChangePasswordCall(data);

        if (result.ok) {
            this.setState({
                inputPasswordOld: '',
                inputPasswordNew: '',
                inputPasswordConfirm: '',
                passwordMismatch: false,
                newPwdInvalid: false,
                newPwdChecks: {
                    meetsLength: false,
                    hasUpper: false,
                    hasSpecial: false,
                    notAllDigits: false,
                    notSequential: false,
                    notCommonWeak: false,
                    noIdentityInPwd: false,
                    differsFromOld: false,
                    ok: false,
                    errors: [],
                    msg: '',
                },
            });
            Swal.fire({
                title: '',
                icon: 'success',
                html: '<p>' + this.props.t('setting.changePassword.success') + '</p>',
                showCancelButton: false,
                confirmButtonColor: '#0A564B',
                confirmButtonText: 'Ok',
            });
        } else {
            Swal.fire({
                title: '',
                icon: 'error',
                text: translateBackendError(this.props.t, result.message),
                showCancelButton: false,
                confirmButtonColor: '#0A564B',
                confirmButtonText: 'Ok',
            });
        }
    };

    render() {
        const {passwordMismatch, newPwdInvalid, newPwdChecks} = this.state;
        const hasEmpty =
            [this.state.inputPasswordOld, this.state.inputPasswordNew, this.state.inputPasswordConfirm]
                .some(v => !v?.trim());


        return (
            <div className="page-content">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-12">
                            <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                                <h4 className="mb-sm-0">{this.props.t('menu.label6')}</h4>
                            </div>
                        </div>
                    </div>

                    {/* Card berada di tengah */}
                    <div className="row justify-content-center">
                        <div className="col-12 col-sm-10 col-md-8 col-lg-6 col-xl-5 col-xxl-4">
                            <div className="card">
                                <div className='card-header'>
                                    <h5 className='card-title mb-0'>
                                        {this.props.t('menu.label7')}
                                    </h5>
                                </div>
                                <div className="card-body p-4">
                                    <div className="d-flex flex-column gap-3">
                                        <PasswordInput
                                            id="oldPasswordInput"
                                            label={this.props.t('setting.changePassword.oldPassword')}
                                            value={this.state.inputPasswordOld}
                                            onChange={this.onInputPasswordOld}
                                            autoComplete="current-password"
                                        />

                                        <div>
                                            <PasswordInput
                                                id="newPasswordInput"
                                                label={this.props.t('setting.changePassword.newPassword')}
                                                value={this.state.inputPasswordNew}
                                                onChange={this.onInputPasswordNew}
                                                autoComplete="new-password"
                                                error={newPwdInvalid}
                                                helperText={
                                                    newPwdChecks.errors && newPwdChecks.errors.length ? (
                                                        <ul className="mb-0 ps-3">
                                                            {newPwdChecks.errors.map((msg, i) => (
                                                                <li key={i}>{msg}</li>
                                                            ))}
                                                        </ul>
                                                    ) : ''
                                                }
                                            />
                                            <PasswordStrengthMeter
                                                pwd={this.state.inputPasswordNew}
                                                t={this.props.t}
                                            />
                                            <PasswordChecklist
                                                meetsLength={newPwdChecks.meetsLength}
                                                hasUpper={newPwdChecks.hasUpper}
                                                hasSpecial={newPwdChecks.hasSpecial}
                                                t={this.props.t}
                                            />
                                        </div>

                                        <PasswordInput
                                            id="confirmPasswordInput"
                                            label={this.props.t('setting.changePassword.confirmPassword')}
                                            value={this.state.inputPasswordConfirm}
                                            onChange={this.onInputPasswordConfirm}
                                            autoComplete="new-password"
                                            error={passwordMismatch}
                                            helperText={this.props.t('setting.changePassword.notMatch')}
                                        />

                                        <div className="text-end">
                                            <button
                                                disabled={hasEmpty || passwordMismatch || newPwdInvalid}
                                                type="button"
                                                className="btn btn-primary"
                                                onClick={() => this.onChangePassword()}
                                            >
                                                {this.props.t('setting.changePassword.button')}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* end tengah */}
                </div>
            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    count: state.counter.value,
    loggedIn: state.loggedIn.value,
    account: state.account.value,
    company: state.company.value,
});

const mapDispatchToProps = {increment, decrement, setLoggedIn, setAccount, setCompany};

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(SettingChangePassword));
